import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LuMessageCircle } from "react-icons/lu";
import axios from "axios";
import { onSocketEvent, offSocketEvent, isSocketConnected } from "../../services/socket";
import "./inboxBell.css";

// Envelope icon + unread badge that links to the /inbox page. Mirrors
// NotificationBell's liveness strategy: the server already fires a
// `notification` event (type "new_message") to each recipient on send, so we
// piggyback on that stream to bump the count — no new socket plumbing.
const InboxBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/v1/messages/unread-count");
      setUnreadCount(data.count);
    } catch (err) {
      // Silent
    }
  }, []);

  // Bump on incoming chat messages relayed as notifications.
  useEffect(() => {
    const handleNotification = (notification) => {
      if (notification.type === "new_message") {
        setUnreadCount((c) => c + 1);
      }
    };
    onSocketEvent("notification", handleNotification);
    return () => offSocketEvent("notification", handleNotification);
  }, []);

  // Fetch on mount, and re-sync on every route change — opening a thread marks
  // it read, so navigating away/back self-corrects any socket over-count.
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount, location.pathname]);

  // Graceful degradation: poll if socket not connected.
  useEffect(() => {
    if (!isSocketConnected()) {
      pollRef.current = setInterval(fetchUnreadCount, 60000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchUnreadCount]);

  return (
    <div className="sr__inbox-bell">
      <button onClick={() => navigate("/inbox")} aria-label="Messages">
        <LuMessageCircle />
        {unreadCount > 0 && (
          <span className="sr__inbox-bell__badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default InboxBell;
